import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { iconMap } from "../../core/iconMap";

function IconByName({ name, size = 16, className }) {
  const Cmp = (name && iconMap[name]) || iconMap.default;
  return <Cmp size={size} className={className} />;
}

export function collectTopNavBinds(control) {
  const binds = [];
  (control?.items || []).forEach((item) => {
    if (item.bind) binds.push(item.bind);
  });
  (control?.groups || []).forEach((g) => {
    (g.columns || []).forEach((col) => {
      if (col.bind) binds.push(col.bind);
    });
  });
  return [...new Set(binds)];
}

function normalizeOption(opt) {
  if (typeof opt === "string") return { label: opt, value: opt };
  if (opt && typeof opt === "object") {
    const label = opt.label ?? opt.value ?? "";
    const value = opt.value ?? opt.label ?? "";
    return { label: String(label), value: String(value) };
  }
  return { label: "", value: "" };
}

function normalizeBrowseLabel(title = "") {
  const t = String(title || "").trim();
  if (!t) return "";
  return t.replace(/^shop by\s+/i, "").trim();
}

export default function TopNavMenu({
  variant = "simple",
  items = [],
  groups = [],
  theme = "",
  inHeader = false,
  openOn = "hover",
  getValue,
  onItemChange,
  onReset,
  onResetAll,
  showReset = true,
  resetLabel = "Reset",
  resetScope = "nav",
  onMegaNavigate,
}) {
  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [openGroupId, setOpenGroupId] = useState(null);
  const [useHoverOpen, setUseHoverOpen] = useState(false);

  const isMega = variant === "mega" && groups.length > 0;

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 901px)");
    const sync = () => setUseHoverOpen(mq.matches && openOn === "hover");
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [openOn]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpenGroupId(null), 220);
  };

  const activeGroupIds = useMemo(() => {
    const set = new Set();
    groups.forEach((g) => {
      const has = (g.columns || []).some((col) => {
        if (!col.bind) return false;
        const v = getValue?.(col.bind);
        return v != null && String(v).trim() !== "";
      });
      if (has) set.add(g.id);
    });
    return set;
  }, [groups, getValue]);

  useEffect(() => {
    if (!isMega || openGroupId == null || useHoverOpen) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpenGroupId(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [isMega, useHoverOpen, openGroupId]);

  useEffect(() => () => clearCloseTimer(), []);

  const handleBindPick = (bind, value) => {
    onItemChange?.({ bind }, value);
    if (isMega) {
      clearCloseTimer();
      setOpenGroupId(null);
      onMegaNavigate?.();
    }
  };

  const openGroup = openGroupId ? groups.find((g) => g.id === openGroupId) : null;
  const openColumns = openGroup?.columns || [];
  const panelBrowseTags = useMemo(() => {
    if (!openColumns.length) return [];
    return [...new Set(openColumns.map((col) => normalizeBrowseLabel(col?.title)).filter(Boolean))];
  }, [openColumns]);
  const panelTitle = openGroup?.panelTitle || `${openGroup?.label || "Shop"} Menu`;
  const panelSubtitle =
    openGroup?.panelSubtitle ||
    (panelBrowseTags.length ? `Browse by ${panelBrowseTags.join(" • ")}` : "Refine your browse experience.");

  const themeClass = theme ? `lfx-topnav-theme-${theme}` : "";

  const onTriggerActivate = (g, active) => {
    clearCloseTimer();
    setOpenGroupId(active ? null : g.id);
  };

  return (
    <div
      ref={rootRef}
      className={`lfx-topnav-root ${isMega ? "lfx-topnav-root--mega" : "lfx-topnav-root--simple"} ${inHeader ? "lfx-topnav-root--header" : ""} ${themeClass}`.trim()}
      onMouseLeave={isMega && useHoverOpen ? scheduleClose : undefined}
    >
      <div className="lfx-topnav-bar" onMouseEnter={isMega && useHoverOpen ? clearCloseTimer : undefined}>
        {isMega ? <div className="lfx-topnav-bar-spacer" aria-hidden /> : null}

        {isMega && (
          <div className="lfx-topnav-triggers">
            {groups.map((g) => {
              const active = openGroupId === g.id;
              const filtered = activeGroupIds.has(g.id);
              return (
                <button
                  key={g.id}
                  type="button"
                  className={`lfx-topnav-trigger ${active ? "is-open" : ""} ${filtered ? "has-filter" : ""}`}
                  aria-expanded={active}
                  aria-haspopup="true"
                  onClick={() => !useHoverOpen && onTriggerActivate(g, active)}
                  onMouseEnter={() => useHoverOpen && (clearCloseTimer(), setOpenGroupId(g.id))}
                >
                  {g.icon ? <IconByName name={g.icon} size={16} className="lfx-topnav-trigger-icon" /> : null}
                  <span className="lfx-topnav-trigger-label">{g.label}</span>
                  <ChevronDown size={13} strokeWidth={2.25} className={`lfx-topnav-chevron ${active ? "is-flip" : ""}`} />
                </button>
              );
            })}
          </div>
        )}

        {!isMega && items.length > 0 && (
          <div className="lfx-topnav-items">
            {items.map((item) => (
              <div key={item.id || item.bind} className="lfx-topnav-item">
                <label className="lfx-topnav-label">
                  {item.icon ? <IconByName name={item.icon} size={14} /> : null}
                  {item.label}
                </label>
                <select
                  className="lfx-select lfx-topnav-select"
                  value={getValue?.(item.bind) || ""}
                  onChange={(e) => onItemChange?.(item, e.target.value)}
                >
                  <option value="">{item.allLabel || "All"}</option>
                  {(item.options || []).map((opt) => {
                    const { label, value } = normalizeOption(opt);
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            ))}
          </div>
        )}

        {isMega && items.length > 0 && (
          <div className="lfx-topnav-items lfx-topnav-items--inline">
            {items.map((item) => (
              <div key={item.id || item.bind} className="lfx-topnav-item lfx-topnav-item--compact">
                <label className="lfx-topnav-label">
                  {item.icon ? <IconByName name={item.icon} size={14} /> : null}
                  {item.label}
                </label>
                <select
                  className="lfx-select lfx-topnav-select"
                  value={getValue?.(item.bind) || ""}
                  onChange={(e) => onItemChange?.(item, e.target.value)}
                >
                  <option value="">{item.allLabel || "All"}</option>
                  {(item.options || []).map((opt) => {
                    const { label, value } = normalizeOption(opt);
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            ))}
          </div>
        )}

        {isMega ? (
          <div className="lfx-topnav-bar-end">
            {showReset ? (
              <button
                type="button"
                className="lfx-btn secondary sm lfx-topnav-reset"
                onClick={() => (resetScope === "all" ? onResetAll?.() : onReset?.())}
              >
                {resetLabel}
              </button>
            ) : null}
          </div>
        ) : (
          showReset && (
            <button
              type="button"
              className="lfx-btn secondary sm lfx-topnav-reset"
              onClick={() => (resetScope === "all" ? onResetAll?.() : onReset?.())}
            >
              {resetLabel}
            </button>
          )
        )}
      </div>

      {isMega && openGroup && (
        <div
          className="lfx-topnav-mega-panel"
          role="region"
          aria-label={openGroup.label}
          onMouseEnter={useHoverOpen ? clearCloseTimer : undefined}
          onMouseLeave={useHoverOpen ? scheduleClose : undefined}
        >
          <div className="lfx-topnav-mega-panel-head">
            <div>
              <p className="lfx-topnav-mega-kicker">{openGroup.label}</p>
              <h3 className="lfx-topnav-mega-title">{panelTitle}</h3>
              <p className="lfx-topnav-mega-subtitle">{panelSubtitle}</p>
            </div>
            {panelBrowseTags.length ? (
              <div className="lfx-topnav-mega-tags" aria-hidden>
                {panelBrowseTags.map((tag) => (
                  <span key={tag} className="lfx-topnav-mega-tag">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <div className="lfx-topnav-mega-columns">
            {openColumns.map((col, ci) => (
              <div key={col.title || col.bind || `col-${ci}`} className="lfx-topnav-mega-col">
                <div className="lfx-topnav-mega-col-head">
                  {col.icon ? <IconByName name={col.icon} size={15} /> : null}
                  <span>{col.title}</span>
                </div>
                {col.bind && col.options?.length ? (
                  <ul className="lfx-topnav-mega-list">
                    {(col.options || []).map((opt) => {
                      const { label, value } = normalizeOption(opt);
                      const cur = getValue?.(col.bind);
                      const isSel = cur != null && String(cur) === String(value);
                      return (
                        <li key={value}>
                          <button
                            type="button"
                            className={`lfx-topnav-mega-link ${isSel ? "is-active" : ""}`}
                            onClick={() => handleBindPick(col.bind, value)}
                          >
                            {label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {col.links?.length ? (
                  <ul className="lfx-topnav-mega-list">
                    {col.links.map((lnk, li) => (
                      <li key={lnk.path || li}>
                        <Link
                          className="lfx-topnav-mega-link lfx-topnav-mega-router"
                          to={lnk.path}
                          onClick={() => {
                            setOpenGroupId(null);
                            onMegaNavigate?.();
                          }}
                        >
                          {lnk.icon ? <IconByName name={lnk.icon} size={14} /> : null}
                          {lnk.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
